// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaShoppingCart, FaBars, FaTimes } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import CartModal from './CartModal';
import MobileMenuAnimation from './MobileMenuAnimation'; // Used for your brand/logo animation
import SparklingStars from './SparklingStars'; // New sparkling stars animation under "Contact Us"
import gsap from 'gsap'; // GSAP for hamburger menu animations

// ----- IMPORT YOUR SEARCHABLE DATA -----
import { allSearchableItems } from '../data/searchData';
// -----------------------------------------

function Navbar() {
  const { cartItems } = useCart();
  const { settings } = useSettings();
  const [cartOpen, setCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [cartPulse, setCartPulse] = useState(false);
  const [menuSeed, setMenuSeed] = useState(0); // Added seed state for SparklingStars

  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();
  // ---------------------

  // Ref for the mobile menu container (for GSAP hamburger menu animation)
  const mobileMenuRef = useRef(null);

  // Effect for cart pulse animation
  useEffect(() => {
    const handleCartPulse = () => {
      setCartPulse(true);
      setTimeout(() => setCartPulse(false), 400);
    };
    window.addEventListener('cartPulse', handleCartPulse);
    return () => window.removeEventListener('cartPulse', handleCartPulse);
  }, []);

  // Update menuSeed when the mobile menu opens
  useEffect(() => {
    if (isMobileMenuOpen) {
      setMenuSeed(prev => prev + 1);
    }
  }, [isMobileMenuOpen]);

  // Filter suggestions based on search query
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const lowerQuery = searchQuery.toLowerCase();
      if (allSearchableItems && allSearchableItems.length > 0) {
        const filtered = allSearchableItems
          .filter(item => item.name.toLowerCase().includes(lowerQuery))
          .slice(0, 10);
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  // Handlers for toggling cart and mobile menu
  const handleCartClick = () => { setCartOpen(true); };
  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      setIsClosing(true);
      setTimeout(() => { setIsMobileMenuOpen(false); setIsClosing(false); }, 300);
    } else {
      setIsMobileMenuOpen(true);
    }
  };
  const closeMobileMenu = (animated = true) => {
    if (!isMobileMenuOpen) return;
    if (animated) {
      setIsClosing(true);
      setTimeout(() => { setIsMobileMenuOpen(false); setIsClosing(false); }, 300);
    } else {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
    }
  };

  // --- Search Handlers ---
  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (trimmedQuery) {
      // Special case: if searching just a number like "8.1" or "G5", search all phones
      const isJustModel = /^[a-z]?\d+\.?\d*$/i.test(trimmedQuery);
      if (isJustModel) {
        navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
        setSearchQuery('');
        setSuggestions([]);
        setShowSuggestions(false);
        if (searchContainerRef.current) {
          const input = searchContainerRef.current.querySelector('input');
          if (input) input.blur();
        }
        return;
      }
      
      // Check if the query matches a specific phone model pattern
      const phoneModelPattern = /^(iphone|samsung|galaxy|google|pixel|oneplus|xiaomi|redmi|poco|oppo|vivo|realme|nokia|motorola|moto|lg|sony|xperia|huawei|honor|asus|htc|blackberry)\s+.+$/i;
      
      if (phoneModelPattern.test(trimmedQuery)) {
        // Extract device type and potential model
        const parts = trimmedQuery.split(' ');
        const deviceBrand = parts[0].toLowerCase();
        const fullModel = trimmedQuery;
        
        // Navigate to the appropriate brand page with model as query parameter
        if (deviceBrand === 'iphone') {
          navigate(`/phone/iphone?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'samsung' || deviceBrand === 'galaxy') {
          // For Galaxy devices, ensure we pass the full name including "Galaxy"
          const modelToPass = deviceBrand === 'galaxy' ? fullModel : fullModel;
          navigate(`/phone/samsung?model=${encodeURIComponent(modelToPass)}`);
        } else if (deviceBrand === 'google' || deviceBrand === 'pixel') {
          navigate(`/phone/pixel?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'oneplus') {
          navigate(`/phone/oneplus?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'xiaomi' || deviceBrand === 'redmi') {
          navigate(`/phone/redmi?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'oppo') {
          navigate(`/phone/oppo?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'huawei') {
          navigate(`/phone/huawei?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'nokia') {
          navigate(`/phone/nokia?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'motorola' || deviceBrand === 'moto') {
          navigate(`/phone/motorola?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'lg') {
          navigate(`/phone/lg?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'sony' || deviceBrand === 'xperia') {
          navigate(`/phone/sony?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'asus') {
          navigate(`/phone/asus?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'htc') {
          navigate(`/phone/htc?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'blackberry') {
          navigate(`/phone/blackberry?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'honor') {
          navigate(`/phone/honor?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'vivo') {
          navigate(`/phone/vivo?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'realme') {
          navigate(`/phone/realme?model=${encodeURIComponent(fullModel)}`);
        } else if (deviceBrand === 'poco') {
          navigate(`/phone/poco?model=${encodeURIComponent(fullModel)}`);
        } else {
          navigate(`/phone?search=${encodeURIComponent(fullModel)}`);
        }
      } else if (trimmedQuery.includes('ipad') || trimmedQuery.includes('galaxy tab')) {
        navigate(`/tablet?model=${encodeURIComponent(trimmedQuery)}`);
      } else if (trimmedQuery.includes('macbook') || trimmedQuery.includes('laptop')) {
        navigate(`/laptop?model=${encodeURIComponent(trimmedQuery)}`);
      } else if (trimmedQuery.includes('watch') || trimmedQuery.includes('apple watch')) {
        navigate(`/watch?model=${encodeURIComponent(trimmedQuery)}`);
      } else if (trimmedQuery.includes('playstation') || trimmedQuery.includes('xbox') || trimmedQuery.includes('nintendo')) {
        navigate(`/console?model=${encodeURIComponent(trimmedQuery)}`);
      } else {
        // Default search behavior
        navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
      }
      
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      if (searchContainerRef.current) {
        const input = searchContainerRef.current.querySelector('input');
        if (input) input.blur();
      }
    }
  };
  const handleSuggestionClick = (suggestion) => {
    // Check if suggestion has a URL first
    if (suggestion.url) {
      navigate(suggestion.url);
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      if (searchContainerRef.current) {
        const input = searchContainerRef.current.querySelector('input');
        if (input) input.blur();
      }
      return;
    }
    
    // Use exactName if available, otherwise use name
    const modelName = suggestion.exactName || suggestion.name;
    
    // Fallback to type-based routing if no URL
    const type = suggestion.type || suggestion.category;
    const name = suggestion.name.toLowerCase();
    
    if (type === 'Phone' || name.includes('iphone') || name.includes('samsung') || name.includes('galaxy')) {
      // Determine the correct route based on the name
      if (name.includes('iphone')) {
        navigate(`/phone/iphone?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('samsung') || name.includes('galaxy')) {
        navigate(`/phone/samsung?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('pixel') || name.includes('google')) {
        navigate(`/phone/pixel?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('oneplus')) {
        navigate(`/phone/oneplus?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('nokia')) {
        navigate(`/phone/nokia?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('motorola') || name.includes('moto')) {
        navigate(`/phone/motorola?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('huawei') || name.includes('honor')) {
        navigate(`/phone/huawei?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('xiaomi') || name.includes('redmi')) {
        navigate(`/phone/xiaomi?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('oppo')) {
        navigate(`/phone/oppo?model=${encodeURIComponent(modelName)}`);
      } else {
        navigate(`/phone?search=${encodeURIComponent(modelName)}`);
      }
    } else if (type === 'Tablet') {
      if (name.includes('ipad')) {
        navigate(`/tablet/ipad?model=${encodeURIComponent(modelName)}`);
      } else if (name.includes('galaxy tab') || name.includes('samsung')) {
        navigate(`/tablet/samsung?model=${encodeURIComponent(modelName)}`);
      } else {
        navigate(`/tablet?model=${encodeURIComponent(modelName)}`);
      }
    } else if (type === 'Laptop') {
      if (name.includes('macbook')) {
        navigate(`/laptop/macbook?model=${encodeURIComponent(modelName)}`);
      } else {
        navigate(`/laptop?model=${encodeURIComponent(modelName)}`);
      }
    } else if (type === 'Watch') {
      navigate(`/watch?model=${encodeURIComponent(modelName)}`);
    } else if (type === 'Console') {
      navigate(`/console?model=${encodeURIComponent(modelName)}`);
    } else {
      // Default fallback
      navigate(`/search?q=${encodeURIComponent(modelName)}`);
    }
    
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (searchContainerRef.current) {
      const input = searchContainerRef.current.querySelector('input');
      if (input) input.blur();
    }
  };
  // Close suggestions when clicking outside the search container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close suggestions on Escape key
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setShowSuggestions(false);
      if (searchContainerRef.current) {
        const input = searchContainerRef.current.querySelector('input');
        if (input) input.blur();
      }
    }
  };
  // Show suggestions on focus if applicable
  const handleFocus = () => {
    if (searchQuery.trim().length > 0 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };
  // ---------------------

  // Mobile menu transform calculation for open/close
  let mobileMenuTransform = 'translateX(100%)';
  if (isMobileMenuOpen && !isClosing) mobileMenuTransform = 'translateX(0)';
  else if (isClosing) mobileMenuTransform = 'translateX(100%)';

  // GSAP Animation for the hamburger menu list items (different effect)
  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      gsap.fromTo(
        mobileMenuRef.current.querySelectorAll('li'),
        { x: 50, opacity: 0, rotation: 5 },
        { x: 0, opacity: 1, rotation: 0, duration: 0.5, stagger: 0.1, ease: 'back.out(1.7)' }
      );
    }
  }, [isMobileMenuOpen]);

  return (
    <>
      <style>
        {`
          /* NAVBAR BASE STYLES - Clean Professional Theme */
          .navbar {
            background: #ffffff;
            color: #6c757d;
            padding: 1rem 2rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
            position: sticky;
            top: 0;
            z-index: 1000;
            box-shadow: 0 4px 6px rgba(0, 123, 255, 0.15);
            border-bottom: 2px solid #007BFF;
            backdrop-filter: blur(10px);
          }
          .nav-brand {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.75rem;
            flex-shrink: 0;
          }
          .brand-link {
            color: #007BFF;
            text-decoration: none;
            transition: all 0.3s ease;
            font-size: 1.25rem;
            margin-bottom: 0;
            display: inline-block;
            font-weight: bold;
          }
          .brand-link:hover {
            color: #3395ff;
            transform: translateY(-1px);
          }
          .tagline {
            margin: 0;
            font-size: 0.8rem;
            color: #6c757d;
            line-height: 1.2;
          }
          .nav-controls {
            display: flex;
            align-items: center;
            gap: 1rem;
            flex-wrap: nowrap;
            margin-left: auto;
          }
          .nav-search {
            display: inline-flex;
            align-items: center;
            margin: 0;
            background-color: rgba(0, 123, 255, 0.05);
            border-radius: 25px;
            transition: all 0.3s ease;
            border: 2px solid #007BFF;
            position: relative;
          }
          .nav-search:focus-within {
            background-color: #ffffff;
            border-color: #3395ff;
            box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
          }
          .nav-search:focus-within .search-input {
            color: #6c757d;
          }
          .nav-search:focus-within .search-input::placeholder {
            color: #6c757d;
          }
          .nav-search:focus-within .search-button {
            color: #007BFF;
          }
          .search-input {
            width: 250px;
            padding: 0.5rem 1rem;
            background: transparent;
            border: none;
            color: #6c757d;
            font-size: 0.9rem;
            outline: none;
            margin: 0;
          }
          .search-input::placeholder {
            color: #6c757d;
          }
          .search-button {
            padding: 0.5rem 1rem;
            background: transparent;
            color: #007BFF;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
          }
          .search-button:hover {
            color: #3395ff;
            transform: scale(1.1);
          }
          .desktop-menu-links {
            display: none;
            gap: 1.5rem;
            list-style: none;
            margin: 0 1rem 0 0;
            padding: 0;
            align-items: center;
          }
          .desktop-menu-links a {
            color: #fff;
            text-decoration: none;
            transition: all 0.3s ease;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            background: linear-gradient(135deg, #007BFF 0%, #3395ff 100%);
            border: 2px solid #007BFF;
            box-shadow: 0 1px 3px rgba(0, 123, 255, 0.1);
            font-weight: 500;
          }
          .desktop-menu-links a:hover {
            background: linear-gradient(135deg, #f8f9fa 0%, #f8f9fa 100%);
            color: #007BFF;
            box-shadow: 0 4px 6px rgba(0, 123, 255, 0.15);
            transform: translateY(-2px);
          }
          .cart-container {
            position: relative;
            display: flex;
            align-items: center;
          }
          .cart-icon-button {
            background: none;
            border: none;
            color: #007BFF;
            cursor: pointer;
            position: relative;
            display: flex;
            align-items: center;
            padding: 5px;
            border-radius: 50%;
            transition: all 0.2s ease;
          }
          .cart-icon-button:hover {
            background-color: rgba(0, 123, 255, 0.1);
            transform: scale(1.1);
          }
          .cart-badge {
            position: absolute;
            top: -4px;
            right: -4px;
            background: red;
            color: #fff;
            border-radius: 50%;
            font-size: 0.7rem;
            padding: 1px 5px;
            line-height: 1;
            font-weight: bold;
            border: 1px solid #fff;
          }
          @keyframes cartPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          @keyframes badgePop {
            0% { transform: scale(1); }
            50% { transform: scale(1.4); }
            100% { transform: scale(1); }
          }
          .cart-pulse .cart-icon-svg {
            animation: cartPulse 0.4s ease;
          }
          .cart-pulse .cart-badge {
            animation: badgePop 0.4s ease;
          }
          .mobile-menu-icon {
            color: #007BFF !important;
            background: none;
            border: none;
            cursor: pointer;
            display: none;
            padding: 5px;
            border-radius: 50%;
            transition: all 0.2s ease;
          }
          .mobile-menu-icon:hover {
            background-color: rgba(0, 123, 255, 0.1);
            transform: scale(1.1);
          }
          .mobile-menu-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background-color: rgba(0,0,0,0.4);
            z-index: 1001;
          }
          .mobile-menu {
            position: fixed;
            top: 0;
            right: 0;
            width: 280px;
            height: 100vh;
            background: linear-gradient(to bottom, #007BFF 0%, #0056b3 100%);
            color: #fff;
            z-index: 1002;
            padding: 1.5rem;
            transition: transform 0.3s ease;
            transform: translateX(100%);
            box-shadow: -3px 0 10px rgba(0, 123, 255, 0.3);
          }
          .mobile-menu ul {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 1.2rem;
          }
          .mobile-menu ul a {
            color: #fff;
            text-decoration: none;
            transition: all 0.2s ease;
            font-size: 1.1rem;
            padding: 0.75rem 1rem;
            display: block;
            border-radius: 6px;
            background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.2);
          }
          .mobile-menu ul a:hover {
            background: rgba(255,255,255,0.2);
            transform: translateX(-5px);
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          }
          .search-suggestions {
            position: absolute;
            top: calc(100% + 5px);
            left: 0;
            right: 0;
            background-color: #ffffff;
            color: #6c757d;
            border: 2px solid #007BFF;
            border-radius: 8px;
            z-index: 999;
            max-height: 300px;
            overflow-y: auto;
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
          }
          .search-suggestions ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .suggestion-item {
            padding: 0.8rem 1rem;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 0.9rem;
            border-bottom: 1px solid #f8f9fa;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .suggestion-item:last-child {
            border-bottom: none;
          }
          .suggestion-item:hover,
          .suggestion-item.highlighted {
            background-color: rgba(0, 123, 255, 0.1);
            color: #007BFF;
          }
          .suggestion-item .type {
            font-size: 0.75rem;
            color: #6c757d;
            margin-left: 0.75em;
            font-style: italic;
            flex-shrink: 0;
          }
          @media (min-width: 992px) {
            .desktop-menu-links { display: flex; }
            .mobile-menu-icon { display: none; }
            .nav-controls { margin-left: 0; }
            .cart-container.mobile-cart { display: none !important; }
            .cart-container.desktop-cart { display: flex !important; }
          }
          @media (max-width: 991px) {
            .navbar { padding: 1rem 1.5rem; }
            .desktop-menu-links { display: none; }
            .mobile-menu-icon { display: inline-flex !important; }
            .nav-controls { flex-grow: 1; justify-content: flex-end; }
            .cart-container.desktop-cart { display: none !important; }
            .cart-container.mobile-cart { display: flex !important; }
          }
          @media (max-width: 768px) {
            .navbar { gap: 0.5rem; align-items: center; }
            .nav-brand { width: 100%; justify-content: center; margin-bottom: 0.75rem; order: -1; }
            .nav-controls { width: 100%; justify-content: center; }
            .nav-search { flex-grow: 1; max-width: 350px; }
            .search-input { width: 100%; }
            .nav-search:focus-within {
              background-color: #fff;
            }
            .nav-search:focus-within .search-input {
              color: #6c757d;
            }
          }
          @media (max-width: 480px) {
            .navbar { padding: 0.8rem 1rem; }
            .nav-brand { gap: 0.5rem; }
            .tagline { font-size: 0.75rem; }
            .nav-controls { gap: 0.5rem; }
            .search-input { padding: 0.4rem 0.8rem; font-size: 0.85rem; }
            .search-button { padding: 0.4rem 0.8rem; }
            .cart-icon-button svg { width: 18px; height: 18px; }
            .mobile-menu-icon svg { width: 22px; height: 22px; }
            .search-suggestions { left: -1rem; right: -1rem; }
          }
        `}
      </style>

      <nav className="navbar">
        {/* Brand Section using MobileMenuAnimation or Custom Logo */}
        <div className="nav-brand">
          <div style={{ width: '40px', height: '40px', flexShrink: 0 }}>
            {settings.logo ? (
              <img 
                src={settings.logo} 
                alt={settings.siteName} 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <MobileMenuAnimation />
            )}
          </div>
          <div>
            <Link to="/" className="brand-link" style={{ color: '#007BFF' }}>{settings.siteName}</Link>
            <p className="tagline" style={{ color: '#6c757d' }}>{settings.tagline}</p>
          </div>
        </div>

        {/* Desktop Menu Links */}
        <ul className="desktop-menu-links">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/about">About Us</Link></li>
          <li><Link to="/location">Our Location</Link></li>
          <li><Link to="/contact">Contact Us</Link></li>
        </ul>

        {/* Controls Area */}
        <div className="nav-controls">
          {/* Search Area */}
          <div className="nav-search" ref={searchContainerRef} onKeyDown={handleKeyDown}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'contents' }}>
              <input
                type="text"
                className="search-input"
                placeholder="Search devices, repairs..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={handleFocus}
                aria-label="Search"
                autoComplete="off"
                aria-haspopup="listbox"
                aria-expanded={showSuggestions && suggestions.length > 0}
              />
              <button type="submit" className="search-button" aria-label="Search">
                <FaSearch size={16} />
              </button>
            </form>
            {showSuggestions && suggestions.length > 0 && (
              <div className="search-suggestions" role="listbox">
                <ul>
                  {suggestions.map((item, index) => (
                    <li
                      key={`${item.type}-${item.name}-${index}`}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(item)}
                      role="option"
                      aria-selected={false}
                    >
                      <span>{item.name}</span>
                      {item.type && <span className="type">({item.type})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Cart Buttons */}
          <div className="cart-container desktop-cart">
            <button
              className={`cart-icon-button ${cartPulse ? 'cart-pulse' : ''}`}
              onClick={handleCartClick}
              id="cart-icon-desktop"
              aria-label={`Shopping Cart: ${cartItems.length} items`}
            >
              <FaShoppingCart size={20} className="cart-icon-svg" />
              {cartItems.length > 0 && (
                <span className="cart-badge">{cartItems.length}</span>
              )}
            </button>
          </div>
          <div className="cart-container mobile-cart" style={{ display: 'none' }}>
            <button
              className={`cart-icon-button ${cartPulse ? 'cart-pulse' : ''}`}
              onClick={handleCartClick}
              id="cart-icon-mobile"
              aria-label={`Shopping Cart: ${cartItems.length} items`}
            >
              <FaShoppingCart size={20} className="cart-icon-svg" />
              {cartItems.length > 0 && (
                <span className="cart-badge">{cartItems.length}</span>
              )}
            </button>
          </div>
          {/* Mobile Menu Icon */}
          <button className="mobile-menu-icon" onClick={toggleMobileMenu} aria-label="Toggle menu">
            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {/* Mobile Sidebar Menu */}
        {(isMobileMenuOpen || isClosing) && (
          <>
            <div className="mobile-menu-overlay" onClick={() => closeMobileMenu(true)} />
            <div
              className="mobile-menu"
              ref={mobileMenuRef}
              style={{ transform: mobileMenuTransform }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => closeMobileMenu(true)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.8rem',
                  cursor: 'pointer'
                }}
                aria-label="Close menu"
              >
                ×
              </button>
              <div style={{ marginTop: '2.5rem' }}>
                <ul>
                  <li><Link to="/" onClick={() => closeMobileMenu(false)}>Home</Link></li>
                  <li><Link to="/about" onClick={() => closeMobileMenu(false)}>About Us</Link></li>
                  <li><Link to="/location" onClick={() => closeMobileMenu(false)}>Our Location</Link></li>
                  <li><Link to="/contact" onClick={() => closeMobileMenu(false)}>Contact Us</Link></li>
                </ul>
                {/* SparklingStars animation appears here under the "Contact Us" item */}
                <SparklingStars seed={menuSeed} />
              </div>
            </div>
          </>
        )}

        {/* Cart Modal */}
        {CartModal && <CartModal show={cartOpen} onClose={() => setCartOpen(false)} />}
      </nav>
    </>
  );
}

export default Navbar;